import React, { useState } from 'react';
import { X, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  job_title: string;
  email: string;
  phone: string;
  number_of_employees: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  number_of_employees?: string;
}

export default function BookDemoModal({ isOpen, onClose }: BookDemoModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    job_title: '',
    email: '',
    phone: '',
    number_of_employees: '',
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.number_of_employees) {
      newErrors.number_of_employees = 'Please select number of employees';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!isSupabaseConfigured || !supabase) {
      setSubmitError('Database connection is not configured. Please try again later.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const phoneNumber = formData.phone.trim() 
        ? parseInt(formData.phone.replace(/\D/g, ''), 10) 
        : null;

      const { error } = await supabase
        .from('demo_requests')
        .insert([
          {
            name: formData.name.trim(),
            job_title: formData.job_title.trim() || null,
            email_id: formData.email.trim(),
            phone_no: phoneNumber,
            no_of_employees: parseInt(formData.number_of_employees.split('-')[1] || formData.number_of_employees.split('-')[0]),
            lead_source: 'landing_page',
            created_at: new Date().toISOString(),
          },
        ]);

      if (error) {
        throw error;
      }

      setSubmitSuccess(true);
      
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error: any) {
      console.error('Error submitting demo request:', error);
      setSubmitError(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      job_title: '',
      email: '',
      phone: '',
      number_of_employees: '',
    });
    setErrors({});
    setSubmitSuccess(false);
    setSubmitError(null);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  if (!isOpen) return null;

  if (submitSuccess) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-white text-center mb-4">
            Thank you!
          </h3>
          <p className="text-slate-300 text-center text-lg">
            We'll contact you very soon!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white">Book a Demo</h3>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-2 bg-slate-800 border ${
                errors.name ? 'border-red-500' : 'border-slate-700'
              } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors`}
              placeholder="John Doe"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-400">{errors.name}</p>
            )}
          </div>

          <div>
            <label htmlFor="job_title" className="block text-sm font-medium text-slate-300 mb-2">
              Job Title
            </label>
            <input
              type="text"
              id="job_title"
              name="job_title"
              value={formData.job_title}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
              placeholder="HR Manager"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              Business Email Address <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-2 bg-slate-800 border ${
                errors.email ? 'border-red-500' : 'border-slate-700'
              } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors`}
              placeholder="john@company.com"
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
              placeholder="+1 (555) 123-4567"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="number_of_employees" className="block text-sm font-medium text-slate-300 mb-2">
              Number of Employees <span className="text-red-400">*</span>
            </label>
            <select
              id="number_of_employees"
              name="number_of_employees"
              value={formData.number_of_employees}
              onChange={handleChange}
              className={`w-full px-4 py-2 bg-slate-800 border ${
                errors.number_of_employees ? 'border-red-500' : 'border-slate-700'
              } rounded-lg text-white focus:outline-none focus:border-emerald-500 transition-colors`}
              disabled={isSubmitting}
            >
              <option value="">Select...</option>
              <option value="0-100">0-100</option>
              <option value="100-500">100-500</option>
              <option value="500-5000">500-5000</option>
            </select>
            {errors.number_of_employees && (
              <p className="mt-1 text-sm text-red-400">{errors.number_of_employees}</p>
            )}
          </div>

          {submitError && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
              <p className="text-sm text-red-400">{submitError}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={handleClose}
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg transition-colors duration-200"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg transition-colors duration-200 flex items-center justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

